import { useCallback, useImperativeHandle, forwardRef } from 'react';
import { useForm, FormProvider, SubmitHandler } from 'react-hook-form';
import { useAtom } from 'jotai';

import { cn, urlObjectToUriString } from '@restworld/utils-common';

import {
  JobListingJpStatusFilter,
  JobListingLocationFilter,
  JobListingRestaurantNameFilter,
  JobListingOccupationFilter,
} from './employer-job-listing-filter.components';
import {
  employerJobListingJpStatusFilterAtom,
  employerJobListingLocationFilterAtom,
  employerJobListingRestaurantNameFilterAtom,
  employerJobListingOccupationFilterAtom,
} from '../employer-job-listing.atoms';
import { useToggleEmployerFiltersModal } from '../../employer-filters-modal';
import { useV2Router } from '../../dashboard/hooks/use-v2-router';

interface EmployerRestuarantFiltersFormValues {
  jpStatus: string[];
  location: string;
  restaurantName: string;
  occupationId: string;
}
export interface IEmployerFilterForm {
  applyFilters: () => void;
  removeFilters: () => void;
}

const EmployerJobListingFilters = forwardRef<IEmployerFilterForm>((_, ref) => {
  const methods = useForm<EmployerRestuarantFiltersFormValues>();
  const { handleSubmit } = methods;
  const [, setEmployerJobListingJpStatusFilter] = useAtom(employerJobListingJpStatusFilterAtom);
  const [, setEmployerJobListingLocationFilter] = useAtom(employerJobListingLocationFilterAtom);
  const [, setEmployerJobListingRestaurantNameFilter] = useAtom(employerJobListingRestaurantNameFilterAtom);
  const [, setEmployerJobListingOccupationFilter] = useAtom(employerJobListingOccupationFilterAtom);
  const { closeEmployerFiltersModal: closeModal } = useToggleEmployerFiltersModal();
  const router = useV2Router();
  const onSubmit: SubmitHandler<EmployerRestuarantFiltersFormValues> = useCallback(
    (values) => {
      setEmployerJobListingJpStatusFilter(values.jpStatus);
      setEmployerJobListingLocationFilter(values.location);
      setEmployerJobListingRestaurantNameFilter(values.restaurantName);
      setEmployerJobListingOccupationFilter(values.occupationId);
      router.push(
        urlObjectToUriString({
          pathname: router.pathname,
          query: { ...values },
        }),
        null,
        {
          shallow: true,
        }
      );
      closeModal();
    },
    [
      closeModal,
      router,
      setEmployerJobListingJpStatusFilter,
      setEmployerJobListingLocationFilter,
      setEmployerJobListingRestaurantNameFilter,
      setEmployerJobListingOccupationFilter,
    ]
  );

  const handleRemove = useCallback(() => {
    setEmployerJobListingJpStatusFilter([]);
    setEmployerJobListingLocationFilter('');
    setEmployerJobListingRestaurantNameFilter('');
    setEmployerJobListingOccupationFilter('');
    closeModal();
    router.push(urlObjectToUriString({ pathname: router.pathname, query: null }), null, { shallow: true });
  }, [
    closeModal,
    router,
    setEmployerJobListingJpStatusFilter,
    setEmployerJobListingLocationFilter,
    setEmployerJobListingRestaurantNameFilter,
    setEmployerJobListingOccupationFilter,
  ]);

  useImperativeHandle(ref, () => ({
    applyFilters: handleSubmit(onSubmit),
    removeFilters: handleRemove,
  }));

  return (
    <FiltersWrapper>
      <FormProvider {...methods}>
        <form action="post" className="flex flex-col justify-between h-full gap-8 mt-8">
          <InputsWrapper>
            <JobListingLocationFilter />
            <JobListingRestaurantNameFilter />
            <JobListingOccupationFilter />
            <JobListingJpStatusFilter />
          </InputsWrapper>
        </form>
      </FormProvider>
    </FiltersWrapper>
  );
});

export default EmployerJobListingFilters;

export const computeJobListingFiltersCount = ({
  restaurantName,
  jpStatus,
  location,
  occupationId,
}: {
  restaurantName?: string;
  jpStatus?: string[];
  location?: string;
  occupationId?: string;
}) => {
  let count = 0;
  if (restaurantName) count += 1;
  if (jpStatus && jpStatus.length > 0) count += 1;
  if (location) count += 1;
  if (occupationId) count += 1;
  return count;
};

const FiltersWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col  h-full', className)} {...props} />
);
const InputsWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-8', className)} {...props} />
);
